import { translationKey } from '../../i18n';
import { localize } from '../../i18n';
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MedRow from './MedRow';
import { colorOf } from '../../utils/medColor';

const DOW_KR = [translationKey('일요일'), translationKey('월요일'), translationKey('화요일'), translationKey('수요일'), translationKey('목요일'), translationKey('금요일'), translationKey('토요일')];


function timeBucket(hour) {
    return localize(hour < 12 ? '오전' : '오후');
}

function formatHourKr(hour, minute) {
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return minute === 0 ? `${h12}:00` : `${h12}:${String(minute).padStart(2, '0')}`;
}

function canDeleteLog(log) {
    if (log.frequencyType === 'AS_NEEDED') return true;
    if (log.status !== 'TAKEN' && log.status !== 'SKIPPED' && log.status !== 'MISSED') return false;
    return new Date(`${log.scheduledDate}T${log.scheduledTime}`) <= new Date();
}

function rowStatus(logStatus) {
    if (logStatus === 'AS_NEEDED') return 'asNeeded';
    switch (logStatus) {
        case 'TAKEN':
            return 'taken';
        case 'MISSED':
            return 'missed';
        case 'SKIPPED':
            return 'skipped';
        default:
            return 'pending';
    }
}

export default function DayDetailCard({
    dateStr, logs, dayInfo, onDeleteLog, deletingIds,
    onMarkMissedTaken, onEditTakenTime, processingIds,
    showSkipReasons = false,
}) {
    const dt = new Date(`${dateStr}T00:00:00`);
    const month = dt.getMonth() + 1;
    const day = dt.getDate();
    const dow = DOW_KR[dt.getDay()];
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const isFutureDate = dateStr > todayStr;

    // 시간대별 그룹핑 (HH:MM 단위)
    const groups = useMemo(() => {
        const map = new Map();
        for (const log of logs) {
            const key = log.scheduledTime.slice(0, 5);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(log);
        }
        return Array.from(map.entries())
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([time, items]) => ({ time, items }));
    }, [logs]);

    const isEmpty = logs.length === 0;

    // 요약 pill — 카운트는 SKIPPED/PRN 제외. PRN은 표시만 하고 "다 먹었는지" 로직에 넣지 않음.
    const counted = logs.filter((l) => l.status !== 'SKIPPED' && l.frequencyType !== 'AS_NEEDED');
    const taken = counted.filter((l) => l.status === 'TAKEN').length;
    const total = counted.length;
    const missedCount = counted.filter((l) => l.status === 'MISSED').length;
    const pendingCount = counted.filter((l) => l.status === 'PENDING').length;

    let pill = null;
    if (total > 0) {
        if (pendingCount === total) {
            pill = { label: localize("{v0}개 예정", { v0: total }), bg: '#f5f5f5', fg: '#525252' };
        } else if (taken === total) {
            pill = { label: localize("{v0} / {v1} 복용", { v0: taken, v1: total }), bg: '#f0fdf4', fg: '#15803d' };
        } else if (taken === 0 && missedCount === total) {
            pill = { label: localize("{v0} / {v1} 복용", { v0: taken, v1: total }), bg: '#fef2f2', fg: '#b91c1c' };
        } else {
            pill = { label: localize("{v0} / {v1} 복용", { v0: taken, v1: total }), bg: '#fffbeb', fg: '#b45309' };
        }
    }

    return (
        <Box sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: isEmpty ? 0 : '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#1a1a1a' }}>
                        {month}{localize("월 ")}{day}{localize("일")}</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#999' }}>{localize(dow)}</Typography>
                </Box>
                {pill && (
                    <Box
                        sx={{
                            fontSize: '11px',
                            fontWeight: 800,
                            padding: '4px 10px',
                            borderRadius: '999px',
                            bgcolor: pill.bg,
                            color: pill.fg,
                        }}
                    >
                        {localize(pill.label)}
                    </Box>
                )}
            </Box>

            {isEmpty ? (
                <Box sx={{ textAlign: 'center', padding: '28px 0 16px' }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '14px',
                            bgcolor: '#f5f5f5',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: '10px',
                        }}
                    >
                        <AccessTimeIcon sx={{ color: '#c4c4c4', fontSize: 24 }} />
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
                        {isFutureDate ? localize("복용할 약이 없는 날이에요") : localize("기록이 없는 날이에요")}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#999', mt: '4px' }}>
                        {isFutureDate ? localize("이 날에는 예정된 약이 없어요") : localize("이 날에는 등록된 약이 없었어요")}
                    </Typography>
                </Box>
            ) : (
                groups.map(({ time, items }, gi) => {
                    const [hh, mm] = time.split(':');
                    const hour = parseInt(hh, 10);
                    const minute = parseInt(mm, 10);
                    return (
                        <Box
                            key={time}
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                py: '12px',
                                borderTop: gi === 0 ? 'none' : '1px solid #f3f4f6',
                            }}
                        >
                            <Box sx={{ width: 44, flexShrink: 0 }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#999' }}>
                                    {timeBucket(hour)}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        color: '#1a1a1a',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {formatHourKr(hour, minute)}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
                                {items.map((log) => (
                                    <MedRow
                                        skipReasonLog={showSkipReasons ? log : undefined}
                                        key={log.id}
                                        logId={log.id}
                                        name={log.medicationName}
                                        color={colorOf(log.medicationId)}
                                        status={rowStatus(log.frequencyType === 'AS_NEEDED' ? 'AS_NEEDED' : log.status)}
                                        scheduledTime={log.scheduledTime}
                                        confirmedAt={log.confirmedAt}
                                        photoUrl={log.imageUrl}
                                        deletable={!!onDeleteLog && canDeleteLog(log)}
                                        deleting={deletingIds?.has(log.id)}
                                        onDelete={() => onDeleteLog && onDeleteLog(log)}
                                        processing={processingIds?.has(log.id)}
                                        onMarkTaken={onMarkMissedTaken && (log.status === 'MISSED' || log.status === 'SKIPPED' || (log.status === 'PENDING' && dateStr < todayStr)) ? () => onMarkMissedTaken && onMarkMissedTaken(log) : undefined}
                                        onEditTime={onEditTakenTime && log.status === 'TAKEN' ? () => onEditTakenTime && onEditTakenTime(log) : undefined}
                                    />
                                ))}
                            </Box>
                        </Box>
                    );
                })
            )}
        </Box>
    );
}
